import { useEffect, useState } from 'react'

import { useMutation, useReactiveVar, useSuspenseQuery } from '@apollo/client'
import FullStory from 'react-fullstory'

import acreConfig from '@acre/config'
import { FullPageLoadingSpinner, UserTracker } from '@acre/design-system'
import { GetCurrentUserDocument, PostBrowserIdDocument } from '@acre/graphql'
import {
  applyFlagOverrides,
  calculateFlags,
  Flag,
  FlagProvider,
  PageScrollHandler,
  readFlagOverrides,
  UserProvider,
} from '@acre/utils'

import { ReportProblemProvider } from '@broker-crm-contexts'

import { caseOverviewLoadingStatus } from './pages/Cases/CaseDetails/CaseOverview/CaseOverview.helpers'
import Eula from './pages/Eula/Eula'
import { useEula } from './pages/Eula/Eula.helpers'
import Router from './routing/Router'
import { introducerType } from './utils/constants'

const App = () => {
  const { data: dataCurrentUser } = useSuspenseQuery<GetCurrentUserQuery>(GetCurrentUserDocument)

  const [isProblemReported, setIsProblemReported] = useState(false)

  const [checkBrowserId] = useMutation<PostBrowserIdMutation>(PostBrowserIdDocument)

  const currentUser = dataCurrentUser.currentUser
  const organisationData = currentUser?.organisation

  // Vite emits vite:preloadError event when it fails to load dynamic imports
  // This event can be used to reload the page to recover from the error
  // https://vite.dev/guide/build.html#load-error-handling
  useEffect(() => {
    window.addEventListener('vite:preloadError', () => {
      // @ts-expect-error - Cypress is a global variable set when running in a Cypress context
      if (window.Cypress) {
        // explicity throw an error that we then handle in Cypress (rather than reloading the page which would cause the test to fail)
        throw new Error('cypress:vite:preloadError')
      }

      // otherwise, try to recover
      window.location.reload()
    })
    // Remove the event listener when the component is unmounted
    return () => {
      window.removeEventListener('vite:preloadError', () => window.location.reload())
    }
  }, [])

  useEffect(() => {
    if (
      currentUser?.role_name === introducerType.introducer ||
      currentUser?.role_name === introducerType.introducerManager
    ) {
      return window.location.assign(acreConfig.INTRODUCER_PORTAL_URL)
    }
  }, [currentUser])

  useEffect(() => {
    checkBrowserId()
  }, [checkBrowserId])

  const {
    loading: loadingEula,
    signedEula,
    doc,
  } = useEula({
    user: currentUser,
  })

  const baseFlags = calculateFlags(organisationData, currentUser)
  // Pre-auth only: once org/user metadata exists it alone decides flag values.
  const calculatedFlags =
    !organisationData && !currentUser ? applyFlagOverrides(baseFlags, readFlagOverrides()) : baseFlags

  // TODO: This needs to be reconsidered as this is the top level loading state for the entire app
  const loadingCaseStatus = useReactiveVar(caseOverviewLoadingStatus)

  if (loadingEula || loadingCaseStatus) {
    return <FullPageLoadingSpinner />
  }

  return (
    <PageScrollHandler>
      <UserProvider value={currentUser}>
        <FlagProvider value={calculatedFlags}>
          <UserTracker user={currentUser}>
            {doc?.data_base64 && !signedEula && <Eula />}
            <Flag keyPath="feature.fullstory" defaultValue={false}>
              <FullStory org={acreConfig.FULLSTORY_ORG_ID} />
            </Flag>
            <ReportProblemProvider value={{ isProblemReported, setIsProblemReported }}>
              <Router />
            </ReportProblemProvider>
          </UserTracker>
        </FlagProvider>
      </UserProvider>
    </PageScrollHandler>
  )
}

export default App
